/* eslint-disable no-prototype-builtins */
import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { LoadOrders, LoadOrderLines, orderAction } from '../../../../features/Order/OrderSlice';
import { loaderActions } from '../../../../features/Loader/LoaderSlice';
import { OverlayTrigger, Form, Tooltip, Alert, Row, Button } from 'react-bootstrap';
import OrderLine from '../../../../components/shared/OrderLine/Index';
import { toast } from 'react-hot-toast';
import API from '../../../../Services/API';
import UploadSingleOrderLines from '../../../../components/shared/Files/UploadSingleOrderLines';
import { downloadTransferOrderLines } from '../../../../Helpers/xlxs/download';
import Swal from 'sweetalert2';
import { formatDate } from '../../../../Helpers/date';
import { validateTransferLine } from '../../../../Helpers/line.validation';
import {
  useCurrentCompanyFullName,
  useCurrentCompanyID
} from '../../../../hooks/useCurrentCompany';
import { sortLinesByErrors } from '../../../../Helpers/sort';
import Transfer from '../../../../components/shared/Order/Transfer';

function TransferOrderId() {
  const { id, vendorType } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    if (vendorType.toLowerCase() !== 'warehouse') {
      toast.error('Transfer orders are only available for Warehouse', { id: 'transfer-orders' });
      navigate('/');
    }
  }, [vendorType]);

  const companyId = useCurrentCompanyID();
  const companyFullName = useCurrentCompanyFullName();
  const dispatch = useDispatch();
  const { currentOrder, linesCount, orderLines } = useSelector((state) => state.order);
  const [search, setSearch] = useState('');
  function refreshPage() {
    fetchCurrentOrder();
  }
  async function fetchCurrentOrder() {
    await dispatch(loaderActions.show());
    await dispatch(
      LoadOrders({
        endpoint: '/transfer_order_header',
        companyId,
        filter: `docNo eq '${id}'`,
        type: 'single'
      })
    );
    if (currentOrder !== undefined) {
      await fetchCurrentOrderLines();
    } else {
      toast.error('No TO found with Order number ' + id);
      navigate(`/transfer-orders${location.search}`);
      await dispatch(loaderActions.hide());
    }
  }

  async function fetchCurrentOrderLines() {
    await dispatch(loaderActions.show());
    await dispatch(
      LoadOrderLines({
        endpoint: '/transfer_order_lines',
        companyId,
        filter: `documentNo eq '${id}'`,
        implementPaginate: false
      })
    );
    return await dispatch(loaderActions.hide());
  }

  useEffect(() => {
    setTimeout(() => {
      fetchCurrentOrder();
    }, 100);
  }, [id]);

  const filteredLines = orderLines.filter(
    (order) =>
      order.itemNo.toLowerCase().includes(search.trim().toLowerCase()) ||
      order.description.toLowerCase().includes(search.trim().toLowerCase())
  );

  async function editValue(documentNo, lineNo, targetField) {
    const targetKey = Object.keys(targetField)[0];
    dispatch(
      orderAction.updateOrderLine({
        documentNo,
        lineNo,
        targetField
      })
    );
    if (targetKey === 'qtyToReceive') {
      let qtyMissing = 0;
      if (targetField[targetKey] > 0) {
        const targetLine = await orderLines.find((line) => line.lineNo === lineNo);
        qtyMissing = targetLine.itemQty - (targetLine.qtyShipped + targetField[targetKey]);
        if (qtyMissing < 0) {
          qtyMissing = 0;
        }
      }
      dispatch(
        orderAction.updateOrderLine({
          documentNo,
          lineNo,
          targetField: { qtyMissing }
        })
      );
    }
  }

  const orderLinesList = filteredLines.map((line, index) => {
    return (
      <OrderLine
        documentNo={line.documentNo}
        line={line}
        index={index}
        vendorType={vendorType}
        key={index}
        type="transfer"
        refresh={refreshPage}
        editValue={editValue}
        showSaveButton={true}
        showOrderLink={false}
      />
    );
  });

  const editedLines = orderLines.reduce((acc, line) => acc + (line.isEditing === true ? 1 : 0), 0);

  async function saveLines(actionType) {
    const validatedLines = orderLines.map((line) => validateTransferLine(line));
    dispatch(orderAction.updateMultipleOrderLines(sortLinesByErrors(validatedLines)));
    if (validatedLines.filter((line) => !line.isValid).length === 0) {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: "Please make sure everything is uploaded properly, You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: `Yes, ${actionType} it!`
      });
      if (result.isConfirmed) {
        dispatch(loaderActions.show());
        const savableLines = orderLines.filter((line) => line.isEditing);
        const batchRequestData = savableLines.map((line) => {
          const data = {};
          line.editedFields.forEach((field) => {
            if (field === 'qtyReceived') {
              data.qtyToReceive = Number(line[field]);
            } else {
              data[field] = line[field];
            }
          });
          return {
            method: 'PATCH',
            url: `transferOrderLinesVP('${line.documentNo}',${line.lineNo},'${line.itemNo}')`,
            headers: {
              Company: companyFullName,
              'Content-Type': 'application/json',
              'If-Match': '*'
            },
            body: data
          };
        });
        if (batchRequestData.length > 0) {
          const res = await API().post('/transfer-batch', { requests: batchRequestData });
          const errors = [];
          res.data.responses.forEach((resp) => {
            if (resp.body && typeof resp.body === 'object' && resp.body.error) {
              errors.push(resp.body.error.message);
            }
          });
          if (errors.length > 0) {
            errors.map((err) => toast.error(err));
          } else if (actionType.toLowerCase() === 'save') {
            toast.success('Order lines saved successfully');
          }
        }
        if (actionType.toLowerCase() === 'post') {
          if (
            currentOrder.hasOwnProperty('scheduledForPosting') &&
            currentOrder.scheduledForPosting === true
          ) {
            if (batchRequestData.length > 0) {
              refreshPage();
            }
            dispatch(loaderActions.hide());
            return Swal.fire({
              icon: 'error',
              title: 'Oops...',
              text: "Your TO is being processed and will be posted soon. Please check back later.\n\nOnce it's posted, it will no longer appear on EVP."
            });
          }
          const res = await API().post(`/transfer_post?(${companyId})?('${id}')`, {
            postDate: formatDate(new Date())
          });
          if (res.data && typeof res.data === 'object' && res.data.error) {
            toast.error(res.data.error.message);
          } else if (res.data && typeof res.data === 'object' && res.data.StatusCode !== 200) {
            toast.error(res.data.Message);
          } else if (
            res.response &&
            res.response.data &&
            res.response.data.error &&
            typeof res.response.data.error === 'object'
          ) {
            toast.error(res.response.data.error.message);
          } else if (res.response && res.response.status >= 400) {
            toast.error(res.response.data.message);
          } else if (res.code === 'ERR_NETWORK') {
            toast.error(res.message);
          } else {
            navigate(`/transfer-orders${location.search}`);
            toast.success('Order lines posted successfully');
          }
        }
        if (batchRequestData.length > 0) {
          refreshPage();
        }
        dispatch(loaderActions.hide());
      }
    } else {
      toast.error('Please fix all lines invalid inputs');
    }
  }

  const ErrorCard = (
    <>
      <Alert variant="warning">
        <> No Transfer order lines for {id}</>
      </Alert>
    </>
  );

  const SearchErrorCard = (
    <>
      <Alert variant="warning">
        <> No Transfer order lines matching current search</>
      </Alert>
    </>
  );

  function downloadXlxs() {
    downloadTransferOrderLines(
      orderLines,
      currentOrder.whseVendorCode,
      `${currentOrder.docNo}-TO-Lines`
    );
  }

  return (
    <div>
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <div>Transfer order detail</div>
          {currentOrder && Object.keys(currentOrder).length > 0 && (
            <Transfer order={currentOrder} vendorType={vendorType} isDetailPage={true} />
          )}
        </div>
      </div>
      {orderLines.length > 0 && (
        <div className=" shadow-sm mb-4">
          <div className="card shadow-sm mb-4">
            <div className="card-body">
              <div className="mb-3 d-flex justify-content-between">
                <div>
                  <span className="me-3">
                    Order Lines ( {filteredLines.length} / {linesCount} )
                  </span>
                  <div className="mt-3 pl-3">
                    <Row>
                      {editedLines ? (
                        <Button variant="info" onClick={() => saveLines('Save')}>
                          Save All
                        </Button>
                      ) : (
                        <></>
                      )}
                      {currentOrder.status === 'Released' ? (
                        <Button
                          variant="primary"
                          className="ml-2"
                          onClick={() => saveLines('Post')}
                        >
                          Post
                        </Button>
                      ) : (
                        <></>
                      )}
                    </Row>
                  </div>
                </div>
                <div className="d-flex">
                  <Form.Control
                    size="sm"
                    type="text"
                    className="mr-2"
                    placeholder="Search "
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {linesCount > 0 ? (
                    <UploadSingleOrderLines
                      type="transfer"
                      vendorType={vendorType}
                      documentNo={id}
                      refresh={refreshPage}
                      orderLines={orderLines}
                      status={
                        currentOrder && Object.keys(currentOrder).length ? currentOrder.status : ''
                      }
                      currentOrder={currentOrder}
                    />
                  ) : (
                    <></>
                  )}
                  {linesCount > 0 ? (
                    <OverlayTrigger
                      key="download"
                      placement={'top'}
                      overlay={<Tooltip id={`tooltip-download`}>Download </Tooltip>}
                    >
                      <i
                        className="feather icon-download  ml-2 mt-2"
                        type="button"
                        style={{ width: '48px', height: '48px' }}
                        onClick={downloadXlxs}
                      />
                    </OverlayTrigger>
                  ) : (
                    <></>
                  )}
                </div>
              </div>
              {currentOrder &&
                currentOrder.hasOwnProperty('jobQueueErrorMessage') &&
                currentOrder.jobQueueErrorMessage !== '' && (
                  <Alert variant="danger">{currentOrder.jobQueueErrorMessage}</Alert>
                )}
            </div>
          </div>
        </div>
      )}
      <div>
        <div>
          {filteredLines.length > 0
            ? orderLinesList
            : orderLines.length > 0
            ? SearchErrorCard
            : ErrorCard}
        </div>
      </div>
    </div>
  );
}

export default TransferOrderId;
